import React, { useState } from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { Card, FormGroup } from "@cofano/cck";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { AirlineStops } from "@mui/icons-material";

import CommentIndicatorButton from "components/comments/CommentIndicatorButton";

import StatusChanger from "view/statuses/components/changer/StatusChanger";
import VoyageCallUpdateTimesAndLocationDialog from "view/voyages/components/dialogs/VoyageCallUpdateTimesAndLocationDialog";
import VoyageCallTimesDetails from "view/voyages/components/details/VoyageCallTimesDetails";
import { CallFromCallSideviewer } from "view/calls/components/sideviewer/useCallSideviewer";

import { hasPluginPermission, usePermission } from "imports/Utils";

import { LocationLink } from "utils/LinkUtils";
import { FormattedDateWindow } from "utils/DateUtils";

import { CommentEntityType, StatusType } from "__generated__/enums";

const CallSideviewerCallBlock = ({
	call,
	refetch,
	openComment,
	forceReadOnly,
}: {
	call: CallFromCallSideviewer;
	refetch: (callId: number) => void;
	openComment: (callId: number, type: CommentEntityType.Call) => void;
	forceReadOnly?: boolean;
}) => {

	const session = useStoreState(store => store.session);

	const [ dialogOpen, openDialog ] = useState(false);

	const { isRTR, isSkipperOrShipManager, isOperator, isReadOnly } = usePermission();

	const hasEditPermission = !isReadOnly && (isRTR || isSkipperOrShipManager || isOperator);

	const isLocationEditable = hasEditPermission &&
		hasPluginPermission(session, "updateActionsAfterPlanning") &&
		hasPluginPermission(session, "planningUpdateCallLocation");

	const timeScheduleEditable = hasPluginPermission(session, "updateActionsAfterPlanning");

	return (
		<React.Fragment>
			<Card
				title={<LocationLink location={call.location} />}
				subtitle={<FormattedDateWindow from={call.mostAccurateArrivalTime} to={call.mostAccurateDepartureTime} />}
				icon={<AirlineStops fontSize="small" color="primary" />}
				collapsable
				actions={isReadOnly ? undefined : [ {
					icon: <EditIcon fontSize="small" />,
					onClick: () => openDialog(true),
					hidden: forceReadOnly || !(timeScheduleEditable && hasEditPermission),
				}, {
					render: () => (
						<CommentIndicatorButton
							entity={call}
							session={session}
							onClick={() => openComment(call.id, CommentEntityType.Call)}
							color="primary"
						/>
					),
				} ]}>
				<React.Fragment>
					<FormGroup label={intl.formatMessage({ id: "STATUS" })} labelPlacement="left-center">
						{call.lastStatusChange && (
							<StatusChanger
								entity={call}
								statusType={StatusType.Call}
								refetch={() => refetch(call.id)}
								showHistory />
						)}
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "LOCATION" })} labelPlacement="left-center">
						<LocationLink location={call.location} />
					</FormGroup>

					<Box mt={3}>
						<VoyageCallTimesDetails call={call} size="small" />
					</Box>
				</React.Fragment>
			</Card>

			<VoyageCallUpdateTimesAndLocationDialog
				open={dialogOpen}
				onClose={() => openDialog(false)}
				call={call}
				refetch={() => refetch(call.id)}
				isLocationEditable={isLocationEditable} />
		</React.Fragment>
	);
};

export default CallSideviewerCallBlock;
